import { Component } from '@angular/core';
import { Observable, forkJoin, firstValueFrom } from 'rxjs';
import * as pdfjsLib from 'pdfjs-dist';
import Swal from 'sweetalert2';
import { helper } from 'src/environments/helper';
import { ScansService } from '../scans.service';
import { UploadResponse, FinalPayload, DocumentChunk } from '../scans.interface';
import { MatDialogRef } from '@angular/material/dialog';

// Configurează worker pentru pdfjs
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';


@Component({
    selector: 'app-add-scans',
    templateUrl: './add-scans.component.html',
    styleUrls: ['./add-scans.component.scss']
})
export class AddScansComponent {
    private readonly MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    private readonly CHUNK_SIZE = 8 * 1024 * 1024; // 8MB

    selectedFiles: File[] = [];
    uploadProgress: { [key: string]: number } = {};
    isUploading = false;
    isLoading = false;
    helper = helper;
    finalPayloads: FinalPayload[] = [];

    constructor(
        private dialogRef: MatDialogRef<AddScansComponent>,
        private scansService: ScansService
    ) { }

    ngOnInit(): void { }

    onFileSelected(event: any): void {
        const files: FileList = event.target.files;

        if (files.length === 0) return;

        const validFiles: File[] = [];
        let hasOversizedFiles = false;
        let hasDuplicateFiles = false;
        const oversizedFiles: string[] = [];
        const duplicateFiles: string[] = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            if (file.type !== 'application/pdf') {
                console.error(`Fișierul ${file.name} nu este PDF`);
                continue;
            }

            if (file.size > this.MAX_FILE_SIZE) {
                hasOversizedFiles = true;
                oversizedFiles.push(file.name);
                continue;
            }

            // Verifică dacă documentul a fost deja încărcat
            if (this.isDocumentAlreadyUploaded(file)) {
                hasDuplicateFiles = true;
                duplicateFiles.push(file.name);
                continue;
            }

            validFiles.push(file);
        }

        // Afișează alertă pentru fișierele prea mari
        if (hasOversizedFiles) {
            const fileList = oversizedFiles.map(name => `• ${name}`).join('\n');
            Swal.fire({
                icon: 'warning',
                title: 'Fișiere prea mari',
                html: `Următoarele fișiere depășesc dimensiunea maximă de 50MB și nu pot fi încărcate:<br><br><div style="text-align: left; font-family: monospace;">${fileList.replace(/\n/g, '<br>')}</div>`,
                confirmButtonText: 'OK',
                confirmButtonColor: '#3085d6'
            });
        }

        // Afișează alertă pentru fișierele duplicate
        if (hasDuplicateFiles) {
            const fileList = duplicateFiles.map(name => `• ${name}`).join('\n');
            Swal.fire({
                icon: 'info',
                title: 'Documente deja încărcate',
                html: `Următoarele documente au fost deja încărcate și vor fi ignorate:<br><br><div style="text-align: left; font-family: monospace;">${fileList.replace(/\n/g, '<br>')}</div>`,
                confirmButtonText: 'OK',
                confirmButtonColor: '#3085d6'
            });
        }

        if (validFiles.length > 0) {
            this.selectedFiles = validFiles;
            // Începe upload-ul automat
            this.uploadFiles();
        }

        // Resetează input-ul pentru a permite selectarea acelorași fișiere din nou
        event.target.value = '';
    }

    async uploadFiles(): Promise<void> {
        if (this.selectedFiles.length === 0) {
            console.error('Nu sunt fișiere selectate');
            return;
        }

        this.isUploading = true;
        // NU mai resetăm finalPayloads - păstrăm documentele existente

        try {
            for (const file of this.selectedFiles) {
                const payload = await this.processFile(file);
                this.finalPayloads.push(payload); // ADAUGĂ la lista existentă
                console.log(`Payload pentru ${file.name}:`, payload);
            }

            console.log('Toate payload-urile:', this.finalPayloads);

        } catch (error) {
            console.error('Eroare la upload:', error);
        } finally {
            this.isUploading = false;
            // Resetează selectedFiles după upload
            this.selectedFiles = [];
        }
    }

    private async processFile(file: File): Promise<FinalPayload> {
        try {
            const totalPages = await this.getPdfPageCount(file);
            const needsChunking = file.size > this.CHUNK_SIZE;

            this.uploadProgress[file.name] = 0;

            if (!needsChunking) {
                return await this.uploadSingleFile(file, totalPages);
            } else {
                return await this.uploadFileInChunks(file, totalPages);
            }
        } catch (error) {
            console.error(`Eroare la procesarea fișierului ${file.name}:`, error);
            throw error;
        }
    }

    private async uploadSingleFile(file: File, totalPages: number): Promise<FinalPayload> {
        const formData = new FormData();
        formData.append('file', file);

        const uploadResponse = await firstValueFrom(this.uploadChunk(formData));

        if (uploadResponse.errors) {
            throw new Error(`Eroare la upload: ${uploadResponse.status_code}`);
        }

        this.uploadProgress[file.name] = 100;

        return {
            document_oficial: {
                filename: uploadResponse.data.filename, // Filename de la server
                original_filename: file.name, // Numele original al fișierului
                ext: uploadResponse.data.ext,
                display_name: this.getDisplayName(file.name),
                numar_chunks: 0, // 0 pentru fișiere fără chunk-uri
                total_pages: totalPages
            },
            document_chunks: [] // Array gol pentru fișiere fără chunk-uri
        };
    }

    private async uploadFileInChunks(file: File, totalPages: number): Promise<FinalPayload> {
        const chunks = this.createFileChunks(file);
        const uploadPromises: Observable<UploadResponse>[] = [];

        chunks.forEach((chunk, index) => {
            const formData = new FormData();
            const chunkFile = new File([chunk], `${this.getFileNameWithoutExt(file.name)}_part${index + 1}.pdf`, {
                type: 'application/pdf'
            });
            formData.append('file', chunkFile);
            uploadPromises.push(this.uploadChunk(formData));
        });

        const uploadResponses = await firstValueFrom(forkJoin(uploadPromises));

        // Verifică dacă toate upload-urile au reușit
        const failedUploads = uploadResponses.filter(response => response.errors);
        if (failedUploads.length > 0) {
            throw new Error(`${failedUploads.length} chunk-uri au eșuat la upload`);
        }

        this.uploadProgress[file.name] = 100;

        return this.buildFinalPayload(file, uploadResponses, totalPages, chunks.length);
    }

    private createFileChunks(file: File): Blob[] {
        const chunks: Blob[] = [];
        let start = 0;

        while (start < file.size) {
            const end = Math.min(start + this.CHUNK_SIZE, file.size);
            chunks.push(file.slice(start, end));
            start = end;
        }

        return chunks;
    }

    private buildFinalPayload(
        originalFile: File,
        uploadResponses: UploadResponse[],
        totalPages: number,
        chunkCount: number
    ): FinalPayload {
        const pagesPerChunk = Math.ceil(totalPages / chunkCount);

        const documentChunks: DocumentChunk[] = uploadResponses.map((response, index) => {
            const pageStart = (index * pagesPerChunk) + 1;
            const pageEnd = Math.min(pageStart + pagesPerChunk - 1, totalPages);

            return {
                filename: response.data.filename,
                original_filename: response.data.original_name,
                ext: response.data.ext,
                fragment_number: index + 1,
                page_start: pageStart,
                page_end: pageEnd,
                total_pages_in_fragment: pageEnd - pageStart + 1
            };
        });

        return {
            document_oficial: {
                filename: null, // Numele original al fișierului (fără _part)
                original_filename: originalFile.name, // Numele original al fișierului (fără _part)
                ext: uploadResponses[0].data.ext,
                display_name: this.getDisplayName(originalFile.name),
                numar_chunks: chunkCount,
                total_pages: totalPages
            },
            document_chunks: documentChunks
        };
    }

    private uploadChunk(formData: FormData): Observable<UploadResponse> {
        return this.scansService.fileUpload(formData);
    }

    private async getPdfPageCount(file: File): Promise<number> {
        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            return pdf.numPages;
        } catch (error) {
            console.error('Eroare la citirea PDF-ului:', error);
            // Fallback: estimare bazată pe dimensiunea fișierului
            // O pagină PDF are în medie ~100KB, dar poate varia mult
            return Math.max(1, Math.ceil(file.size / (100 * 1024)));
        }
    }

    private getDisplayName(filename: string): string {
        return filename.replace(/\.[^/.]+$/, '').replace(/_/g, ' ');
    }

    private getFileNameWithoutExt(filename: string): string {
        return filename.replace(/\.[^/.]+$/, '');
    }

    // Metodă pentru submit cu payload-ul final - TOATE documentele într-un singur request
    async submitDocuments(): Promise<void> {
        if (this.finalPayloads.length === 0) {
            console.error('Nu există documente uploadate pentru submit');
            return;
        }

        this.isLoading = true;

        try {
            console.log('Payload pentru submit:', this.finalPayloads);

            const response = await firstValueFrom(
                this.scansService.submitFiles(this.finalPayloads)
            );

            console.log('Toate documentele au fost submise cu succes:', response);

            this.dialogRef.close({ success: true });

        } catch (error) {
            console.error('Eroare la submit:', error);
            throw error;
        } finally {
            this.isLoading = false;
        }
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    // Metodă helper pentru a obține progresul total
    getTotalProgress(): number {
        const progresses = Object.values(this.uploadProgress);
        if (progresses.length === 0) return 0;

        const total = progresses.reduce((sum, progress) => sum + progress, 0);
        return Math.round(total / progresses.length);
    }

    // Metodă pentru a formata dimensiunea fișierului
    getFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    // Metodă pentru a șterge un fișier selectat (înainte de upload)
    removeSelectedFile(index: number): void {
        this.selectedFiles.splice(index, 1);
    }

    // Metodă pentru a șterge un document uploadat
    removeUploadedFile(index: number): void {
        this.finalPayloads.splice(index, 1);
    }

    // Metodă pentru a verifica dacă un document a fost deja încărcat
    private isDocumentAlreadyUploaded(file: File): boolean {
        return this.finalPayloads.some(payload =>
            payload.document_oficial.original_filename === file.name
        );
    }

    // Metodă pentru a reseta componenta
    reset(): void {
        this.selectedFiles = [];
        this.uploadProgress = {};
        this.finalPayloads = [];
        this.isUploading = false;
    }

    // Metodă pentru a reseta doar documentele uploadate (fără a reseta starea de upload)
    clearUploadedDocuments(): void {
        this.finalPayloads = [];
    }
}
